import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Flex } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { radius } from "#app/theme/v2/radius";
import { shadows } from "#app/theme/v2/shadows";
import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Text } from "#components/atoms/Typographies/Text";
import { Company } from "#modules/company/infra/Company.dto";
import { getIndustrialProcessWording } from "#shared/helpers/getIndustrialProcessWording";
import { Icon } from "#shared/icons/Icon";

type DefaultProcessStep = Company["companySettings"]["defaultProcessSteps"][number];

type DefaultProcessStepDragOverlayProps = {
  processStep: DefaultProcessStep;
};

export const DefaultProcessStepDragOverlay = ({
  processStep,
}: DefaultProcessStepDragOverlayProps) => (
  <Flex
    align="center"
    gap={spacing[2]}
    px={spacing[4]}
    py={spacing[4]}
    style={{
      backgroundColor: colors.bg.elevation.primary,
      borderRadius: radius.md,
      boxShadow: shadows.elevation3,
      cursor: "grabbing",
    }}
  >
    <Text size="small" type="medium" truncate>
      {getIndustrialProcessWording(processStep.industrialProcess)}
    </Text>
    <Text size="small" color={colors.text.secondaryDefault} truncate>
      {processStep.description}
    </Text>
  </Flex>
);

type DefaultProcessStepItemProps = {
  processStep: DefaultProcessStep;
  position: number;
  onDelete: () => void;
};

export const DefaultProcessStepItem = ({
  processStep,
  position,
  onDelete,
}: DefaultProcessStepItemProps) => {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: processStep.id,
  });
  const dragHandleLabel = t("onboardingV2.defaultProcessSteps.dragHandle");

  return (
    <Flex
      ref={setNodeRef}
      align="center"
      gap={spacing[4]}
      h={spacing[16]}
      px={spacing[6]}
      w="100%"
      style={{
        position: "relative",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
        zIndex: isDragging ? 1 : 0,
      }}
    >
      <Flex
        align="center"
        justify="center"
        w={spacing[8]}
        h={spacing[8]}
        style={{
          flexShrink: 0,
          backgroundColor: colors.bg.elevation.tertiary,
          borderRadius: radius.md,
        }}
      >
        <Text size="small" type="medium" color={colors.text.secondaryDefault}>
          {position}
        </Text>
      </Flex>

      <Flex
        align="center"
        gap={spacing[2]}
        pl={spacing[2]}
        pr={spacing[4]}
        py={spacing[4]}
        style={{
          flex: 1,
          minWidth: 0,
          backgroundColor: colors.bg.elevation.primary,
          borderRadius: radius.md,
          boxShadow: shadows.elevation3,
        }}
      >
        <Flex
          align="center"
          p={spacing[2]}
          aria-label={dragHandleLabel}
          style={{
            flexShrink: 0,
            color: colors.icon.secondary,
            cursor: isDragging ? "grabbing" : "grab",
            touchAction: "none",
          }}
          {...attributes}
          {...listeners}
        >
          <Icon name="gripVertical" size={16} />
        </Flex>
        <Text size="small" type="medium" truncate style={{ flex: 1, minWidth: 0 }}>
          {getIndustrialProcessWording(processStep.industrialProcess)}
        </Text>
        <Text
          size="small"
          color={colors.text.secondaryDefault}
          truncate
          style={{ flex: 1, minWidth: 0 }}
        >
          {processStep.description}
        </Text>
        <Tooltip label={t("common.delete")}>
          <IconButton
            variant="transparent"
            size="sm"
            aria-label={t("common.delete")}
            onClick={onDelete}
          >
            <Icon name="trash" size={16} />
          </IconButton>
        </Tooltip>
      </Flex>
    </Flex>
  );
};
